import { useState, useEffect } from 'react';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * Réhaut breakpoints
 * ─────────────────────────────────────────────────────────────────────
 *   mobile   < 640px            phones (iPhone 14 Pro 393×852, SE 375×667)
 *   tablet   640–1024px         iPad portrait (768), 7" Android tablets
 *   desktop  > 1024px            iPad landscape, laptops, monitors
 *
 * Tablet doctrine
 *   • Shell (DashboardLayout) treats tablet as mobile — hamburger nav,
 *     mobile menu overlay, mobile profile dropdown. Driven by a single
 *     `isMobile = bp === 'mobile' || bp === 'tablet'` at the top of
 *     DashboardLayout.tsx.
 *   • Page content treats tablet as desktop — every page-level
 *     `isMob = bp === 'mobile'` returns false on iPad portrait. Pages
 *     get the desktop multi-pane layout, with the surrounding shell
 *     using the touch-friendly hamburger.
 *
 * The hybrid is intentional: clinic staff use iPad landscape (1024+ →
 * desktop everywhere) for clinical work where multi-pane is valuable,
 * and iPad portrait gets the desktop content under a hamburger shell
 * because the typical use case is consultation read-only / viewing.
 *
 * SSR returns 'desktop' (window is undefined). Pages that need to
 * avoid hydration flicker for purely cosmetic spacing should use the
 * --page-gutter / --page-pad CSS variables defined in globals.css
 * rather than branching on this hook in inline styles.
 *
 * Returns current breakpoint based on window.innerWidth.
 */
export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    return w < 640 ? 'mobile' : w <= 1024 ? 'tablet' : 'desktop';
  });

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setBp(w < 640 ? 'mobile' : w <= 1024 ? 'tablet' : 'desktop');
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return bp;
}

/** Shared style helper: make any modal full-screen on mobile */
export const mobileModalStyle = (bp: Breakpoint): React.CSSProperties =>
  bp === 'mobile'
    ? { inset: 0, maxWidth: 'none', maxHeight: 'none', borderRadius: 0, margin: 0, width: '100%', height: '100%', overflowY: 'auto' }
    : {};

/** Format number as Mexican peso currency string */
export const fmtMXN = (n: number) => `$${n.toLocaleString('es-MX')}`;

/** Toggle an item in a Set, returning a new Set */
export function toggleSet<T>(set: Set<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

// groupServicesByArea lives in group-services.ts (no React import) so server
// code — the in-app firma receipt route — can use it; re-exported here for the
// existing client callers.
export { groupServicesByArea, type GroupedService } from './group-services';

// Spanish pluralization for unit labels
export function pluralizeUnit(count: number, singular: string): string {
  const raw = (singular || 'unidad').trim();
  const s = raw.toLowerCase();
  if (count === 1) return `${count} ${raw}`;
  // Already plural
  if (s.endsWith('es') || s.endsWith('s')) return `${count} ${raw}`;
  // Preserve case for suffix
  const isUpper = raw[0] === raw[0].toUpperCase();
  // z → ces
  if (s.endsWith('z')) { const base = raw.slice(0, -1); return `${count} ${base}${isUpper ? 'Ces' : 'ces'}`; }
  // ón → ones
  if (s.endsWith('ón')) { const base = raw.slice(0, -2); return `${count} ${base}${isUpper ? 'Ones' : 'ones'}`; }
  // vowel → +s
  if (/[aeiou]$/i.test(s)) return `${count} ${raw}s`;
  // consonant → +es
  return `${count} ${raw}es`;
}
